"use client";

// NettiChat — THE conversation surface with netti, app-wide. One grammar
// (law_netti_chat), inherited from the netti space's exchange: your words read
// quiet (--ink-3, a touch smaller), netti's answer reads in full ink — no
// bubbles, no boxes, everything left-aligned. The streaming message reveals
// word-by-word, each word blurring into view as it lands (SSE deltas for
// ask-netti; a client reveal for the resonance conductor — same look). The
// composer is BARE: no border, no box, no field — you just start typing at the
// bottom. Scroll follows the live edge only while the reader is at it (the
// message-scroller behaviour), never yanking a reader who scrolled up.

import * as React from "react";
import OrganismMark from "../brand/OrganismMark";
import NettiMarkdown from "./NettiMarkdown";
import ChatLinkRows from "./ChatLinkRows";
import ChatReplies from "./ChatReplies";
import { useResumable } from "../../lib/os/resume";

/** does this answer carry markdown worth drawing? plain lines keep the cheap path */
const MARKDOWN_RE = /[`*_#|>\n]|\]\(https?:|^\s*\d+[.)]\s|^\s*[-+]\s/m;

export type { ChatLink } from "../../lib/netti/links";
import type { ChatLink } from "../../lib/netti/links";
import { uiForPath } from "../../lib/netti/linkActions";

/** a card netti hangs on one of its own lines: the connect walk's steps
 *  (components/netti/connect), drawn by the host's `renderCard`. Only a
 *  reference rides the message; the card reads its live state itself. */
export type ChatCard =
  | { kind: "connect"; walk: string; step: string }
  | { kind: "connect-pick" };

export type ChatMsg = {
  id: string;
  who: "netti" | "you" | "notice";
  text: string;
  /** a card under netti's line (cleanCard keeps only these shapes) */
  card?: ChatCard;
  /** places netti located this turn, drawn as rows in the list language under
   *  the message (lib/netti/links), so a "show me the latest X" answer is
   *  always actionable. */
  links?: ChatLink[];
  /** on a netti message: the one to three replies it offered above the
   *  composer (lib/netti/links cleanReplies). Only the newest are drawn. */
  replies?: string[];
  /** on your message: it was one of netti's offered replies, tapped */
  via?: "chip";
};

type Props = {
  msgs: ChatMsg[];
  typing?: boolean;
  /** The masked activity line while netti works ("reading pricing…") — shown
   *  beside the thinking smudges, never the model's own process talk. */
  status?: string | null;
  onSend?: (text: string) => void;
  /** Composer disabled (netti resting / a pick is pending). */
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** The message currently streaming in — its words reveal + blur into view. */
  streamingId?: string | null;
  /** Inline slot rendered after the stream (chips, onboarding controls). */
  children?: React.ReactNode;
  /** Compact rhythm for embedded mounts (the netti space). */
  compact?: boolean;
  /** The one message that carries EMPHASIS — the same words, one step up in
   *  size, still inline in the stream. There are exactly two type sizes here
   *  (chat and emphasis); nothing else may introduce a third. */
  emphasisId?: string | null;
  /** The ask-netti corner variant: the composer types right-aligned, wrapped —
   *  you speak from the right, netti answers from the left. */
  rightHand?: boolean;
  /** Render the netti mark beside the field as the send control — the corner
   *  affordance's grammar (law_netti_corner): the organism mark alone, in one
   *  ink, never inside a disc. */
  sendMark?: boolean;
  /** stop netti mid-thought (what it already said stays). Shown as a quiet
   *  word while it works or speaks. */
  onStop?: () => void;
  /** the words already in the composer when it mounts (the dock keeps a draft
   *  alive across the past view, which unmounts this surface). */
  initialDraft?: string;
  /** every change to the composer's words, so a host can ask "is there a draft?"
   *  at the moment it matters instead of reaching into the textarea. */
  onDraftChange?: (draft: string) => void;
  /** keep the unsent line in this browser under this key (lib/os/places
   *  nettiDraft), so a reload, a closed tab or a re-mount gives it back. Only
   *  the dock's one real composer passes it; a send empties it, sign-out forgets it. */
  resumeKey?: string | null;
  /** the one to three replies netti offers next, drawn right above the
   *  composer while netti rests (ChatReplies). Opt-in: a host that passes
   *  none shows none. */
  replies?: string[];
  /** a reply was tapped: send it as the person's words, marked as a pick */
  onReply?: (text: string) => void;
  /** draw a message's card (the connect walk). A host that passes none shows none. */
  renderCard?: (card: ChatCard, msgId: string) => React.ReactNode;
};

// split into word + whitespace tokens; each word animates in on mount. Stable
// keys (by index) mean already-revealed words never re-animate as text grows.
function RevealText({ text }: { text: string }): React.JSX.Element {
  const tokens = React.useMemo(() => text.split(/(\s+)/), [text]);
  return (
    <>
      {tokens.map((t, i) =>
        /\s/.test(t) || t === "" ? (
          <React.Fragment key={i}>{t}</React.Fragment>
        ) : (
          <span key={i} className="nchat-word">{t}</span>
        ),
      )}
    </>
  );
}

// inline memory links — netti can reference [a space](/projects/…) inside its
// sentence. Internal paths (leading "/") render as quiet living-colour links
// that carry you there live (the shell's netti-nav listener does the moving).
// Anything else stays plain text — never an external jump from netti's mouth.
function RichText({ text, reveal = false }: { text: string; reveal?: boolean }): React.JSX.Element {
  const parts = React.useMemo(() => {
    const out: Array<{ kind: "text" | "link"; text: string; path?: string }> = [];
    let last = 0;
    // fresh regex per parse — a shared /g regex carries lastIndex state across
    // renders (and the compiler rightly forbids mutating module scope).
    const LINK_RE = /\[([^\]\n]{1,80})\]\((\/[^)\s]{1,200})\)/g;
    for (let m = LINK_RE.exec(text); m; m = LINK_RE.exec(text)) {
      if (m.index > last) out.push({ kind: "text", text: text.slice(last, m.index) });
      out.push({ kind: "link", text: m[1], path: m[2] });
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
    return out;
  }, [text]);
  return (
    <>
      {parts.map((p, i) =>
        p.kind === "link" ? (
          <button
            key={i}
            type="button"
            className="nchat-link"
            onClick={() => window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path: p.path } }))}
          >
            {p.text}
          </button>
        ) : reveal ? (
          <RevealText key={i} text={p.text} />
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        ),
      )}
    </>
  );
}

/** where a located place goes. A tab of a panel (`/settings?view=data`), a
 *  connection's guided window and a page on the web are not paths the shell can
 *  glide to, so the UI bridge performs them (lib/netti/linkActions uiForPath);
 *  everything else the shell glides to. */
function openLink(path: string, link?: ChatLink): void {
  const ui = uiForPath(path, link && link.path === path ? link.kind : undefined);
  if (ui) {
    window.dispatchEvent(new CustomEvent("innernet:netti-ui", { detail: ui }));
    return;
  }
  window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path } }));
}

type RowProps = { m: ChatMsg; reveal: boolean; em: boolean; renderCard?: Props["renderCard"] };

// one message, memoised: typing in the composer (or another message growing)
// never re-draws a settled answer, its code blocks or its tables. A card reads
// its own live state (it subscribes itself), so the row can stay memoised.
const MessageRow = React.memo(function MessageRow({ m, reveal, em, renderCard }: RowProps): React.JSX.Element {
  const md = m.who === "netti" && MARKDOWN_RE.test(m.text);
  return (
    <div className={`nchat-m nchat-m--${m.who}${em ? " nchat-m--em" : ""}${md ? " nchat-m--md" : ""}${m.card ? " nchat-m--card" : ""}`}>
      {m.who === "netti"
        ? md
          ? <NettiMarkdown text={m.text} reveal={reveal} />
          : <RichText text={m.text} reveal={reveal} />
        : m.text}
      {m.who === "netti" && !reveal && m.links && m.links.length > 0 && (
        // the places netti found, as rows in the list language: each carries
        // you straight there (the shell's netti-nav listener does the moving;
        // a page on the web opens beside the dashboard), so a located list is
        // always actionable whatever the prose says.
        <ChatLinkRows links={m.links} onOpen={openLink} />
      )}
      {/* the card arrives once its line has been said, like the rows do */}
      {m.who === "netti" && !reveal && m.card && renderCard?.(m.card, m.id)}
    </div>
  );
});

export default function NettiChat({
  msgs,
  typing = false,
  status = null,
  onSend,
  disabled = false,
  placeholder = "say something to netti…",
  autoFocus = false,
  streamingId = null,
  children,
  compact = false,
  emphasisId = null,
  rightHand = false,
  sendMark = false,
  onStop,
  initialDraft = "",
  onDraftChange,
  resumeKey = null,
  replies,
  onReply,
  renderCard,
}: Props): React.JSX.Element {
  const [draft, setDraft] = useResumable<string>(resumeKey, initialDraft);
  // a line given back from the browser is told to the host once, so it knows a draft is there
  // (the dock's fold guard asks before folding the rail over it)
  const toldRef = React.useRef(false);
  React.useEffect(() => {
    if (!resumeKey || toldRef.current || !draft) return;
    toldRef.current = true;
    onDraftChange?.(draft);
  }, [draft, resumeKey, onDraftChange]);
  // a composer that has focus or holds words is never taken away from the person
  // typing in it: `disabled` then means read-only, and it lands for real the
  // moment they have both let go (netti resting must not eat a half-written line).
  const [focused, setFocused] = React.useState(false);
  const locked = disabled && !focused && draft.length === 0;
  const streamRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  // follow-the-live-edge: only auto-scroll while the reader sits at the bottom
  const stickRef = React.useRef(true);

  const scrollToEnd = React.useCallback((smooth = true) => {
    const el = streamRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  // Only a READER's own scroll lets go of the live edge. Our smooth scroll toward
  // the end fires scroll events mid-flight while words keep arriving, and reading
  // those as "they scrolled up" dropped the follow: a long walk's newest beat and
  // its chips were left below the fold (2026-10-22).
  const handAtRef = React.useRef(0);
  const markHand = React.useCallback(() => {
    handAtRef.current = performance.now();
  }, []);
  // a press or a key on an action inside the stream (a beat's "next", a link) moves
  // the conversation on rather than scrolling it: it re-engages the live edge, like
  // a send. Read as a hand, it let go of the follow and the next beat's own chips
  // were left below the fold (the walk's "enter innernet", 2026-10-22).
  const onHand = React.useCallback((e: React.SyntheticEvent) => {
    const t = e.target as Element | null;
    if (t && typeof t.closest === "function" && t.closest('button, a, [role="button"]')) {
      stickRef.current = true;
      return;
    }
    markHand();
  }, [markHand]);
  const onScroll = React.useCallback(() => {
    const el = streamRef.current;
    if (!el) return;
    const atEdge = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (atEdge) stickRef.current = true;
    else if (performance.now() - handAtRef.current < 700) stickRef.current = false;
  }, []);

  // new content settles the view to the live edge ONLY if we're following it
  React.useEffect(() => {
    if (stickRef.current) scrollToEnd(true);
  }, [msgs, typing, children, scrollToEnd]);

  // ...and words revealing inside a message grow it without a new message, so the
  // live edge is also followed as the stream's content grows (one rAF, instant):
  // a long walk's newest beat and its chips can never sit below the fold.
  React.useEffect(() => {
    const el = streamRef.current;
    if (!el || typeof MutationObserver === "undefined") return;
    let raf = 0;
    const follow = () => {
      raf = 0;
      if (stickRef.current) el.scrollTop = el.scrollHeight;
    };
    const mo = new MutationObserver(() => {
      if (!raf) raf = requestAnimationFrame(follow);
    });
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  React.useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus();
  }, [autoFocus, disabled]);

  const change = (v: string) => {
    setDraft(v);
    onDraftChange?.(v);
  };

  const send = () => {
    const text = draft.trim();
    if (!text || disabled || !onSend) return;
    change("");
    stickRef.current = true; // sending re-engages the live edge
    onSend(text);
  };

  return (
    <div className={`nchat${compact ? " nchat--compact" : ""}${rightHand ? " nchat--right" : ""}`}>
      <div
        className="nchat-stream"
        ref={streamRef}
        onScroll={onScroll}
        onWheel={markHand}
        onTouchMove={markHand}
        onPointerDown={onHand}
        onKeyDown={onHand}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {msgs.map((m) => (
          <MessageRow key={m.id} m={m} reveal={m.id === streamingId} em={m.id === emphasisId} renderCard={renderCard} />
        ))}
        {children}
        {typing && (
          <div className="nchat-typing" aria-label={status ?? "netti is thinking"}>
            <i /><i /><i />
            {status && (
              // the masked activity line — what netti is doing, in its own
              // words, keyed so a new activity re-settles in (never a jump).
              <span key={status} className="nchat-status" aria-live="polite">{status}…</span>
            )}
            {onStop && (
              <button type="button" className="nchat-stop" onClick={onStop}>stop</button>
            )}
          </div>
        )}
        {!typing && streamingId && onStop && (
          <button type="button" className="nchat-stop nchat-stop--live" onClick={onStop}>stop</button>
        )}
      </div>

      {onReply && replies && replies.length > 0 && !typing && !streamingId && !disabled && (
        <ChatReplies
          replies={replies}
          quiet={draft.trim().length > 0}
          onPick={(text) => {
            if (disabled) return;
            stickRef.current = true; // a pick re-engages the live edge, like a send
            onReply(text);
          }}
        />
      )}

      {onSend && (
        <div className="nchat-composer">
          <textarea
            ref={inputRef}
            className="nchat-input"
            rows={1}
            value={draft}
            disabled={locked}
            readOnly={disabled && !locked}
            placeholder={placeholder}
            aria-label="message netti"
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(e) => change(e.target.value)}
            onKeyDown={(e) => {
              // an IME confirming a candidate with Enter (or cancelling with Esc)
              // is the keyboard talking to the IME, not to netti.
              if (e.nativeEvent.isComposing) return;
              if (e.key === "Escape" && onStop && (typing || streamingId)) {
                // stop is the whole of this Esc: it must not ALSO fold the rail
                // (the dock's window listener) or close a sheet behind it.
                e.preventDefault();
                e.stopPropagation();
                onStop();
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          {sendMark && (
            <button
              type="button"
              className="nchat-sendmark"
              aria-label="send to netti"
              disabled={disabled}
              onClick={send}
            >
              <OrganismMark size={22} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
